
import { useState } from "react";
import { CloseGreyIcon } from "../../../assets/icons";
import { useAuth } from "../../../auth/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { CreateReservationDTO } from "../../../models/reservation/reservation.request";
import { validateEmail } from "../../../utils/validators";
import { reservationService } from "../../../services/reservationService";
import { useNotification } from "../../toster-notification/NotificationContext";
import Loader from "../../shared/Loader";
import { handleErrorNotification } from "../../../utils/errorNotificatioMessage";
import UnauthorizedUserModal from "../../shared/UnauthorizedUserModal";
import { motion } from "framer-motion";

interface ReservationViewProps {
    primaryColor?: string;
    onClose: () => void;
}

const ReservationView: React.FC<ReservationViewProps> = ({ onClose, primaryColor }) => {

    const [reservationInfo, setReservationInfo] = useState<CreateReservationDTO>({
        fullName: "",
        phoneNumber: "",
        email: "",
        forAnotherPerson: false,
        reservationDate: new Date(),
        departureDate: undefined,
        numberOfPeople: 1,
        description: "",
        tableId: undefined,
        localId: "",
        userId: ""
    });

    const [fullNameError, setFullNameError] = useState<string>("");
    const [phoneNumberError, setPhoneNumberError] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [numberOfPeopleError, setNumberOfPeopleError] = useState<string>("");
    const [reservationDateError, setReservationDateError] = useState<string>("");
    const [reservationDepartureDateError, setReservationDepartureDateError] = useState<string>("");

    const [loading, setLoading] = useState<boolean>(false);

    const { user } = useAuth();

    const { localId } = useParams<{ localId: string }>();

    const { notify } = useNotification();

    
    const handleCreateReservation = async () => {

        setFullNameError("");
        setPhoneNumberError("");
        setEmailError("");
        setNumberOfPeopleError("");
        setReservationDateError("");
        setReservationDepartureDateError("");

        let isValid = true;

        if (reservationInfo.forAnotherPerson && reservationInfo.fullName.trim() === "") {
            setFullNameError("Ime i prezime su obavezni.");
            isValid = false;
        }

        if (reservationInfo.forAnotherPerson && reservationInfo.phoneNumber.trim() === "") {
            setPhoneNumberError("Broj telefona je obavezan.");
            isValid = false;
        }

        let validation = validateEmail(reservationInfo.email);

        if (reservationInfo.forAnotherPerson && validation) {
            setEmailError(validation);
            isValid = false;
        }

        if (reservationInfo.numberOfPeople <= 0) {
            setNumberOfPeopleError("Broj osoba mora biti veći od nule.");
            isValid = false;
        }

        if (!reservationInfo.reservationDate) {
            setReservationDateError("Datum rezervacije je obavezan.");
            isValid = false;
        }

        if (reservationInfo.departureDate && reservationInfo.departureDate < reservationInfo.reservationDate) {
            setReservationDepartureDateError("Datum odlaska ne može biti pre datuma rezervacije.");
            isValid = false;
        }

        if (!isValid) {
            return;
        }

        if (!localId) {
            notify("error", "Nije izabran lokal za rezervaciju.");
            return;
        }

        if (!user || !user.id) {
            notify("error", "Korisnik nije prijavljen.");
            return;
        }

        setLoading(true);
        try {
            reservationInfo.userId = user.id;
            reservationInfo.localId = localId;

            const response = await reservationService.createReservation(reservationInfo);

            if (response.success) {
                notify("success", "Uspešno kreirana rezervacija.");
                onClose();
            } else {
                notify("error", response.message || "Neuspešno kreiranje rezervacije.");
            }
        } catch (error: any) {
            notify("error", "Neuspešno kreiranje rezervacije.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    }

    const pad2 = (n: number) => String(n).padStart(2, "0");

    const toLocalDateInput = (d: Date) =>
        `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

    const toLocalTimeInput = (d: Date) =>
        `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

    if (!localId) {
        return <div>Nije izabran lokal za rezervaciju.</div>
    }

    if (!user || !user.id) {
        return <UnauthorizedUserModal
            onClose={onClose}
        />;
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <section className="modal-section reservation" onClick={onClose}>
            <div className="w-layout-blockcontainer container w-container">
                <motion.section
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 12 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-wrapper">
                        <div className="modal-div" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header-div">
                                <div>
                                    <div className="modal-header">Podaci o rezervaciji</div>
                                    <div className="description-text">
                                        Popunite formu ispod i očekujte potvrdu naše rezervacije<br />
                                    </div>
                                </div>
                                <div className="modal-button-div" onClick={onClose}>
                                    <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                                </div>
                            </div>
                            <div className="modal-form-block w-form">
                                <form className="modal-form">

                                    <div className={`small-box-div disable pointer ${reservationInfo.forAnotherPerson ? "green-bg" : ""}`} onClick={() => setReservationInfo({ ...reservationInfo, forAnotherPerson: !reservationInfo.forAnotherPerson })}>
                                        <div>Rezervacija za drugu osobu</div>
                                    </div>

                                    {reservationInfo.forAnotherPerson && (
                                        <>
                                            <div className="modal-form-div-group">
                                                <div className="modal-group-header">Vaši podaci</div>

                                                <div className="modal-form-div">
                                                    <label className="modal-form-label">Ime i prezime *</label>
                                                    <input className="form-input grey w-input" placeholder="Unesite ime i prezime" type="text"
                                                        value={reservationInfo.fullName}
                                                        onChange={(e) => setReservationInfo({ ...reservationInfo, fullName: e.target.value })}
                                                    />
                                                    {fullNameError && <div className="error-text">{fullNameError}</div>}
                                                </div>

                                                <div className="div-grid-2">
                                                    <div className="modal-form-div">
                                                        <label className="modal-form-label">Broj telefona *</label>
                                                        <input className="form-input grey w-input" placeholder="Unesite broj telefona" type="tel"
                                                            value={reservationInfo.phoneNumber}
                                                            onChange={(e) => setReservationInfo({ ...reservationInfo, phoneNumber: e.target.value })}
                                                        />
                                                        {phoneNumberError && <div className="error-text">{phoneNumberError}</div>}
                                                    </div>

                                                    <div className="modal-form-div">
                                                        <label className="modal-form-label">Email</label>
                                                        <input className="form-input grey w-input" placeholder="Unesite email adresu" type="email"
                                                            value={reservationInfo.email}
                                                            onChange={(e) => setReservationInfo({ ...reservationInfo, email: e.target.value })}
                                                        />
                                                        {emailError && <div className="error-text">{emailError}</div>}
                                                    </div>
                                                </div>

                                            </div>
                                            <div className="modal-hr"></div>
                                        </>
                                    )}

                                    <div className="modal-form-div-group">
                                        <div className="modal-group-header">Detalji rezervacije</div>
                                        <div className="div-grid-2">
                                            <div className="modal-form-div">
                                                <label className="modal-form-label">Datum *</label>
                                                <input className="form-input grey w-input" type="date"
                                                    value={reservationInfo.reservationDate ? toLocalDateInput(reservationInfo.reservationDate) : ""}
                                                    onChange={(e) => {
                                                        const picked = e.target.value; // "YYYY-MM-DD"
                                                        if (!picked)
                                                            return setReservationInfo({ ...reservationInfo, reservationDate: new Date() });
                                                        const base = reservationInfo.reservationDate ?? new Date();
                                                        const [y, m, d] = picked.split("-").map(Number);
                                                        const next = new Date(base);
                                                        next.setFullYear(y, m - 1, d);

                                                        setReservationInfo({ ...reservationInfo, reservationDate: next });
                                                    }}
                                                    min={toLocalDateInput(new Date())}
                                                />
                                                {reservationDateError && <div className="error-text">{reservationDateError}</div>}
                                            </div>

                                            <div className="modal-form-div">
                                                <label className="modal-form-label">Broj osoba *</label>
                                                <input className="form-input grey w-input" placeholder="Unesite broj osoba" type="number"
                                                    value={reservationInfo.numberOfPeople}
                                                    onChange={(e) => setReservationInfo({ ...reservationInfo, numberOfPeople: parseInt(e.target.value) })}
                                                    min={1}
                                                />
                                                {numberOfPeopleError && <div className="error-text">{numberOfPeopleError}</div>}
                                            </div>
                                        </div>

                                        <div className="div-grid-2">
                                            <div className="modal-form-div">
                                                <label className="modal-form-label">Vreme dolaska *</label>
                                                <input className="form-input grey w-input" placeholder="Unesite vreme dolaska" type="time"
                                                    value={reservationInfo.reservationDate ? toLocalTimeInput(reservationInfo.reservationDate) : ""}
                                                    onChange={(e) => {
                                                        const [hours, minutes] = e.target.value.split(":").map(Number);
                                                        const base = reservationInfo.reservationDate ?? new Date();
                                                        const next = new Date(base);
                                                        next.setHours(hours, minutes, 0, 0);
                                                        setReservationInfo({ ...reservationInfo, reservationDate: next });
                                                    }}
                                                />
                                            </div>
                                            <div className="modal-form-div">
                                                <label className="modal-form-label">Vreme odlaska</label>
                                                <input className="form-input grey w-input" placeholder="Unesite vreme odlaska" type="time"
                                                    value={reservationInfo.departureDate ? toLocalTimeInput(reservationInfo.departureDate) : ""}
                                                    onChange={(e) => {
                                                        const [hours, minutes] = e.target.value.split(":").map(Number);
                                                        const base = reservationInfo.departureDate ?? new Date(reservationInfo.reservationDate);
                                                        const next = new Date(base);
                                                        next.setHours(hours, minutes, 0, 0);
                                                        setReservationInfo({ ...reservationInfo, departureDate: next });
                                                    }}
                                                />
                                                {reservationDepartureDateError && <div className="error-text">{reservationDepartureDateError}</div>}
                                            </div>
                                        </div>

                                        <div className="modal-form-div">
                                            <label className="modal-form-label">Napomena</label>
                                            <textarea placeholder="Unesite napomenu (npr. Porodični ručak)" className="form-textarea grey w-input"
                                                value={reservationInfo.description}
                                                onChange={(e) => setReservationInfo({ ...reservationInfo, description: e.target.value })}
                                            ></textarea>
                                        </div>

                                    </div>
                                </form>
                            </div>
                            <div className="modal-buttons">
                                <div className="button-secondary medium" onClick={onClose}>
                                    <div>Otkaži</div>
                                </div>
                                <div className="button-primary gradient" onClick={() => handleCreateReservation()}>
                                    <div>Pošalji rezervaciju</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.section>
            </div>
        </section >

    );

};

export default ReservationView;